import { Colors } from "@/enums/Colors";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import CountriesService, { CountryData } from "@/services/countries/CountriesService";
import { AddOutlined } from "@mui/icons-material";
import { Box, FormLabel, InputAdornment, MenuItem, Skeleton, TextField, Typography, styled } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Control, Controller, FieldValues, UseFormHandleSubmit, useForm } from "react-hook-form";
import { InformationRowContainer } from "../../lazo-one-settings-components/profile-components/CompanyInformation";
import { InformationFormContainer } from "../../lazo-one-settings-components/profile-components/PersonalInformationEditForm";
import CommonButton from "../../utils/CommonButton";
import LazoModal from "../../utils/LazoModal";

import CommonFormControl from "@/components/utils/CommonFormControl";
import { getStatesByCountry } from "@/components/utils/Countries/CountriesStaticStatesDataUtils";
import { CommonStyledSelect, CommonStyledSelectMenuProps } from "@/components/utils/SelectStyles";
import { LazoCompanyAccountType } from "@/enums/LazoCompanyAccountType";
import { TextFieldInput } from "@/external/ensolvers-core-frontend-mui-v5/components/FormInputs/TextFieldInput";
import StringUtils from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/StringUtils";
import { CompanyOnboarding } from "@/interfaces/onboarding-interfaces/CompanyOnboarding";
import { UserOnboarding } from "@/interfaces/onboarding-interfaces/UserOnboarding";
import { NewCustomerDTO } from "@/models/customers/NewCustomerDTO";
import CustomerManagementService from "@/services/CustomerManagementService";
import { LazoInvitation } from "@/types/LazoInvitation";
import { EnumsUtils } from "@/utils/TaskEnumUtils";
import { textFieldStyles } from "../../onboarding-components/LazoOnboarding";
import { OnboardingCheckbox } from "../../onboarding-components/OnboardingCheckbox";
import { FINAL_CATEGORIES, INDUSTRIES, NUMBER_OF_EMPLOYESS, YEARS } from "../../onboarding-components/OnboardingCompanyForm";
import { CreateCompanyAsModal } from "./CreateCompanyAsModal";
import { OwnerCompanyUserForm } from "./OwnerCompanyUserForm";

const FinancialInputField = ({ label, propertyName, control, value }) => {
  return (
    <Container className="form-item">
      <Typography className="gray-text">{label}</Typography>
      <TextFieldInput
        propertyName={propertyName}
        control={control}
        disabled={false}
        InputProps={{
          id: `qa-lazo-${propertyName}`,
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                color: value ? Colors.LIGHT_GRAY_8A8E93 : Colors.BLACK_151517,
              }}
            >
              USD
            </InputAdornment>
          ),
        }}
        TextFieldProps={{
          sx: { width: "100%", ...textFieldStyles },
          inputProps: {
            type: "number",
            min: "0",
          },
        }}
      />
    </Container>
  );
};

interface CompanyInformationFormProps {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  control: Control<any>;
  isOpen: boolean;
  setCountryNames: Dispatch<SetStateAction<string[]>>;
  setCountries: Dispatch<SetStateAction<CountryData[]>>;
  setIsLoadingCountry: Dispatch<SetStateAction<boolean>>;
  register: any;
  selectedCountry: any;
  setSelectedCountry: Dispatch<SetStateAction<any>>;
  countryNames: string[];
  countries: CountryData[];
  statesNames: string[];
  fetchAndSetStates: (selectedCountry: CountryData) => void;
  handleSubmit: UseFormHandleSubmit<FieldValues, undefined>;
  createCompany: (companyData: CompanyOnboarding) => void;
  loadingStates: boolean;
  isLoadingCountry: boolean;
  handlePreviousStep: () => void;
}

//TODO:Move this to a sepparate file, this is a component
function CompanyInformationForm(props: CompanyInformationFormProps) {
  return (
    <>
      <CommonButton
        sx={{
          height: "2.5rem",
          whiteSpace: "nowrap",
          paddingInline: "16px",
          borderRadius: "0.5rem",
        }}
        endIcon={<AddOutlined />}
        onClick={() => props.setIsOpen(true)}
      >
        <Typography fontWeight={600}>Create Company</Typography>
      </CommonButton>
      <LazoModal
        open={props.isOpen}
        handleClose={() => props.setIsOpen(false)}
        title="Create new Customer"
        sx={{
          width: "650px",
          "& .content-container": {
            height: "27.25rem !important",
            overflowY: "auto",
            "& > div": {
              height: "inherit",
            },
          },
        }}
        buttons={
          <>
            <CommonButton purpleBorderVariant onClick={props.handlePreviousStep}>
              Go back
            </CommonButton>
            <CommonButton onClick={props.handleSubmit(props.createCompany)}>Next</CommonButton>
          </>
        }
      >
        <>
          <InformationFormContainer>
            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Company Name</FormLabel>
                <Controller
                  control={props.control}
                  name={"companyName"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      sx={{ width: "100%", ...textFieldStyles }}
                      {...props.register("companyName", { required: true })}
                      defaultValue={value || ""}
                      placeholder={error && "companyName required"}
                      onChange={onChange}
                      error={!!error}
                      value={value}
                    />
                  )}
                />
              </CommonFormControl>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Legal Name</FormLabel>
                <Controller
                  control={props.control}
                  name={"legalName"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      sx={{ width: "100%", ...textFieldStyles }}
                      {...props.register("legalName", { required: true })}
                      placeholder={error && "Legal name required"}
                      onChange={onChange}
                      error={!!error}
                      value={value || ""}
                    />
                  )}
                />
              </CommonFormControl>
            </InformationRowContainer>
            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <Controller
                  control={props.control}
                  name={"description"}
                  render={({ field: { onChange, value }, fieldState: { error: descriptionError } }) => (
                    <>
                      <FormLabel>Short Company Description </FormLabel>
                      <TextField
                        sx={{ width: "100%", ...textFieldStyles }}
                        {...props.register("description", {
                          required: true,
                        })}
                        placeholder={descriptionError && "   Short Company Description"}
                        onChange={onChange}
                        error={!!descriptionError}
                        value={value || ""}
                      />
                    </>
                  )}
                />
              </CommonFormControl>

              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Zip Code</FormLabel>
                <Controller
                  control={props.control}
                  name={"zipCode"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      sx={{ width: "100%", ...textFieldStyles }}
                      {...props.register("zipCode", { required: true })}
                      defaultValue={value}
                      placeholder={error && "Zip Code required"}
                      onChange={onChange}
                      error={!!error}
                      value={value || ""}
                    />
                  )}
                />
              </CommonFormControl>
            </InformationRowContainer>

            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Country</FormLabel>
                <Controller
                  name="country"
                  control={props.control}
                  defaultValue={props.selectedCountry?.name}
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => {
                    return !props.isLoadingCountry ? (
                      <CommonStyledSelect
                        {...props.register("country", { required: true })}
                        value={value || ""}
                        MenuProps={CommonStyledSelectMenuProps}
                        error={!!error}
                        onChange={(event) => {
                          const selectedCountryName = event.target.value;
                          const selectedCountryData = props.countries.find((country) => country.name === selectedCountryName);
                          props.setSelectedCountry(selectedCountryData);
                          props.fetchAndSetStates(selectedCountryData);
                          onChange(event);
                        }}
                      >
                        {props.countryNames.map((country) => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                      </CommonStyledSelect>
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        sx={{
                          borderRadius: "0.75rem",
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    );
                  }}
                />
              </CommonFormControl>

              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>State</FormLabel>
                <Controller
                  name="state"
                  control={props.control}
                  defaultValue={props.selectedCountry?.name}
                  rules={{ required: true }}
                  render={({ field: { value }, fieldState: { error } }) => {
                    return !props.loadingStates ? (
                      <CommonStyledSelect
                        {...props.register("state", { required: true })}
                        value={value || ""}
                        error={!!error}
                        MenuProps={CommonStyledSelectMenuProps}
                      >
                        {props.statesNames.map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </CommonStyledSelect>
                    ) : (
                      <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: "0.75rem" }} />
                    );
                  }}
                />
              </CommonFormControl>
            </InformationRowContainer>
            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>City</FormLabel>
                <Controller
                  control={props.control}
                  name={"city"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      sx={{ width: "100%", ...textFieldStyles }}
                      {...props.register("city", { required: true })}
                      placeholder={error && "City required"}
                      onChange={onChange}
                      error={!!error}
                      value={value || ""}
                    />
                  )}
                />
              </CommonFormControl>

              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Address</FormLabel>
                <Controller
                  control={props.control}
                  name={"address"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      sx={{ width: "100%", ...textFieldStyles }}
                      {...props.register("address", { required: true })}
                      placeholder={error && "Address required"}
                      onChange={onChange}
                      error={!!error}
                      value={value || ""}
                    />
                  )}
                />
              </CommonFormControl>
            </InformationRowContainer>

            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Website</FormLabel>
                <Controller
                  control={props.control}
                  name={"website"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      sx={{ margin: 0, ...textFieldStyles, width: "100%" }}
                      {...props.register("website", { required: false })}
                      placeholder={error && "Website required"}
                      onChange={onChange}
                      error={!!error}
                      value={value || ""}
                    />
                  )}
                />
              </CommonFormControl>

              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Telephone</FormLabel>
                <Controller
                  control={props.control}
                  name={"telephone"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      sx={{ margin: 0, width: "100%", ...textFieldStyles }}
                      {...props.register("telephone", { required: false })}
                      placeholder={error && "Telephone required"}
                      onChange={onChange}
                      error={!!error}
                      value={value || ""}
                    />
                  )}
                />
              </CommonFormControl>
            </InformationRowContainer>
            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Key Investors</FormLabel>
                <Controller
                  control={props.control}
                  name={"keyInvestors"}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <TextField
                      sx={{ margin: 0, ...textFieldStyles }}
                      placeholder={error ? "Key Investors required" : "Enter key investors"}
                      onChange={onChange}
                      error={!!error}
                      value={value || ""}
                    />
                  )}
                />
              </CommonFormControl>

              <CommonFormControl sx={{ flex: "0.5", width: "50%", marginTop: "1.5rem" }}>
                <OnboardingCheckbox label="Delaware C-Corp" propertyName="delawareCCorp" control={props.control} />
              </CommonFormControl>
            </InformationRowContainer>

            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Latest Financial Stage</FormLabel>
                <Controller
                  control={props.control}
                  name={"latestFinancialStage"}
                  render={({ field: { value }, fieldState: { error } }) => (
                    <CommonStyledSelect
                      {...props.register("latestFinancialStage", {
                        required: true,
                      })}
                      value={value || ""}
                      MenuProps={CommonStyledSelectMenuProps}
                      error={!!error}
                    >
                      {Object.entries(FINAL_CATEGORIES.sort()).map(([_, value]) => (
                        <MenuItem key={value} value={value}>
                          {StringUtils.capitalizeWords(EnumsUtils.parseEnumToRender(value))}
                        </MenuItem>
                      ))}
                    </CommonStyledSelect>
                  )}
                />
              </CommonFormControl>

              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Year Founded</FormLabel>
                <Controller
                  control={props.control}
                  name={"yearFounded"}
                  render={({ field: { value }, fieldState: { error } }) => (
                    <CommonStyledSelect
                      {...props.register("yearFounded", { required: true })}
                      value={value || ""}
                      error={!!error}
                      MenuProps={CommonStyledSelectMenuProps}
                    >
                      {YEARS.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </CommonStyledSelect>
                  )}
                />
              </CommonFormControl>
            </InformationRowContainer>

            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>N of Employees</FormLabel>
                <Controller
                  control={props.control}
                  name={"numberOfEmployees"}
                  render={({ field: { value }, fieldState: { error } }) => (
                    <CommonStyledSelect
                      {...props.register("numberOfEmployees", {
                        required: true,
                      })}
                      error={!!error}
                      value={value || ""}
                      MenuProps={CommonStyledSelectMenuProps}
                    >
                      {Object.entries(NUMBER_OF_EMPLOYESS).map(([_, value]) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </CommonStyledSelect>
                  )}
                />
              </CommonFormControl>

              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Industry</FormLabel>
                <Controller
                  control={props.control}
                  name={"industry"}
                  render={({ field: { value }, fieldState: { error } }) => (
                    <CommonStyledSelect
                      {...props.register("industry", { required: true })}
                      value={value || ""}
                      error={!!error}
                      MenuProps={CommonStyledSelectMenuProps}
                    >
                      {Object.entries(INDUSTRIES).map(([_, value]) => (
                        <MenuItem key={value} value={value}>
                          {EnumsUtils.parseEnumToRender(value)}
                        </MenuItem>
                      ))}
                    </CommonStyledSelect>
                  )}
                />
              </CommonFormControl>
            </InformationRowContainer>

            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>EBDITA</FormLabel>
                <FinancialInputField label="" propertyName="ebitda" control={props.control} value={""} />
              </CommonFormControl>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Cash on Hand</FormLabel>
                <FinancialInputField label="" propertyName="cashOnHand" control={props.control} value={""} />
              </CommonFormControl>
            </InformationRowContainer>

            <InformationRowContainer gap={"inherit"}>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Monthly Revenue</FormLabel>
                <FinancialInputField label="" propertyName="monthlyRevenue" control={props.control} value={""} />
              </CommonFormControl>
              <CommonFormControl sx={{ flex: "0.5", width: "50%" }}>
                <FormLabel>Total Funding Received</FormLabel>
                <FinancialInputField label="" propertyName="totalFundingReceived" control={props.control} value={""} />
              </CommonFormControl>
            </InformationRowContainer>
          </InformationFormContainer>
        </>
      </LazoModal>
    </>
  );
}

export default function CreateLazoCompanyModal() {
  const [isOpen, setIsOpen] = useState(false);

  const [countries, setCountries] = useState<CountryData[]>([]);
  const [countryNames, setCountryNames] = useState<string[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<CountryData>();
  const [statesNames, setStatesNames] = useState<string[]>([]);
  const [loadingStates, setLoadingStates] = useState<boolean>(false);
  const [isLoadingCountry, setIsLoadingCountry] = useState(false);
  const [accountType, setAccountType] = useState<LazoCompanyAccountType>(LazoCompanyAccountType.COMPANY);
  const [workspaceName, setWorkspaceName] = useState<string>("");
  const [companyData, setCompanyData] = useState<CompanyOnboarding>();
  const [ownerUserData, setOwnerUserData] = useState<UserOnboarding>();
  const [userMembers, setUserMembers] = useState<LazoInvitation[]>([]);
  const [step, setStep] = useState<number>(1);

  const [creating, setCreating] = useState<boolean>(false);

  useMemo(() => {
    const fetchCountries = async () => {
      setIsLoadingCountry(true);
      await CountriesService.getCountriesList().then((response) => {
        response.onSuccess(() => {
          const countryList = response.getContentOrThrowError();
          setCountryNames(countryList.map((country) => country.name));
          setCountries(countryList);
          setIsLoadingCountry(false);
        });
        response.onError(() => {
          NotificationService.error("Error fetching countries");
        });
      });
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      fetchAndSetStates(selectedCountry);
    }
  }, [selectedCountry]);

  const fetchAndSetStates = (selectedCountry: CountryData) => {
    setLoadingStates(true);
    if (selectedCountry) {
      CountriesService.getStatesList(selectedCountry?.iso2)
        .then((apiStates) => {
          if (apiStates.getContentOrThrowError().length == 0) {
            const staticStates = getStatesByCountry(selectedCountry.iso2);
            setStatesNames(staticStates.map((state) => state.name));
          } else {
            setStatesNames(apiStates.getContentOrThrowError().map((state) => state.name));
          }
        })
        .finally(() => setLoadingStates(false));
    }
  };

  const handleNextStep = () => {
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handlePreviousStep = () => {
    if (step > 0) {
      if (accountType === LazoCompanyAccountType.COMPANY) {
        setStep(step - 1);
      } else {
        setStep(step - 2);
      }
    }
  };

  const handleAccountType = (data: LazoCompanyAccountType) => {
    setAccountType(data);
    if (data === LazoCompanyAccountType.COMPANY) {
      setStep(2);
    } else {
      setStep(3);
    }
  };

  function createCompany(companyData: CompanyOnboarding) {
    if (companyData.telephone === "") {
      companyData.telephone = null;
    }
    setCompanyData(companyData);
    handleNextStep();
  }

  const { register, handleSubmit, control, reset } = useForm();

  function handleSetIsOpen(open: boolean) {
    setIsOpen(open);
    if (!open) {
      setStep(1);
      reset();
    }
  }

  useEffect(() => {
    if (ownerUserData !== undefined && ownerUserData) {
      handleCreateCompany();
    }
  }, [ownerUserData, userMembers]);

  const handleCreateCompany = () => {
    let createCompany: NewCustomerDTO = {
      accountType: accountType,
      workspaceName: workspaceName,
      companyOnboarding: companyData,
      userCreation: ownerUserData,
      invitations: userMembers,
    };

    setCreating(true);
    CustomerManagementService.createCustomer(createCompany)
      .then((response) => {
        response
          .onSuccess(() => {
            NotificationService.info("Company created successfully");
            setIsOpen(false);
            setUserMembers([]);
            setStep(1);
            setOwnerUserData(undefined);
            reset();
          })
          .onError((_) => {
            NotificationService.error(response.getMessage());
          });
      })
      .finally(() => setCreating(false));
  };

  const steps = [
    {
      step: 1,
      component: <CreateCompanyAsModal isOpen={isOpen} setIsOpen={handleSetIsOpen} handleAccountType={handleAccountType} />,
    },
    {
      step: 2,
      component: (
        <CompanyInformationForm
          setIsOpen={handleSetIsOpen}
          control={control}
          isOpen={isOpen}
          handleSubmit={(callback) => handleSubmit(callback)}
          createCompany={createCompany}
          loadingStates={loadingStates}
          isLoadingCountry={isLoadingCountry}
          selectedCountry={selectedCountry}
          setSelectedCountry={setSelectedCountry}
          countryNames={countryNames}
          countries={countries}
          statesNames={statesNames}
          register={register}
          fetchAndSetStates={fetchAndSetStates}
          setIsLoadingCountry={setIsLoadingCountry}
          setCountryNames={setCountryNames}
          setCountries={setCountries}
          handlePreviousStep={handlePreviousStep}
        />
      ),
    },
    {
      step: 3,
      component: (
        <OwnerCompanyUserForm
          creating={creating}
          step={step}
          setStep={setStep}
          handlePreviousStep={handlePreviousStep}
          handleNextStep={handleNextStep}
          companyData={companyData}
          ownerUserData={ownerUserData}
          setOwnerUserData={setOwnerUserData}
          setUserMembers={setUserMembers}
          userMembers={userMembers}
          handleCreateCompany={handleCreateCompany}
          isOpen={isOpen}
          setIsOpen={handleSetIsOpen}
          accountType={accountType}
          setWorkspaceName={setWorkspaceName}
        />
      ),
    },
  ];

  return <>{steps.find((s) => s.step === step)?.component}</>;
}

const Container = styled(Box)(() => ({
  "&.lazo-one-role-container": {
    paddingLeft: "10px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    "& .checkbox-container": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      columnGap: "0.9375rem",
    },
    "& .lazo-one-role-checkbox": {
      width: "1rem",
      height: "1rem",
      "&.MuiCheckbox-root": {
        color: `${Colors.LIGHT_GRAY_DEDEE1}`,
        "& .MuiSvgIcon-root": {
          fontSize: "1.15rem",
        },
      },
    },
  },
}));
